import {isDefined} from './utils'

/**
 * @class JobQueue
 *
 * Schedules batches of functions to be executed after a set number
 * of milliseconds. Exposes a single method, `run`, which is used to both add functions
 * to the queue and execute existing batches of functions
 *
 * @param options.queueInterval? Number of milliseconds in between execution of a batch of functions.
 *        Defaults to 16ms
 * @param options.batchSize? Number of functions to be executed in a single batch.
 *        Defaults to 50 functions
 */
class JobQueue {
  constructor(options = {queueInterval: 16, batchSize: 50}) {
    this.run = this.run.bind(this)
    this.queue = []
    this.queueInterval = options.queueInterval
    this.batchSize = options.batchSize
  }

  run(fn) {
    if (isDefined(fn)) {
      // Defer the first call if we have an empty queue otherwise this is no
      // better than calling the given function inline. setTimeout of 0 will schedule
      // `run` to execute at the top of the next stack frame
      if (this.queue.length === 0) {
        setTimeout(this.run, 0)
      }
      return this.queue.push(fn)
    } else {
      // We'll run through the queue in batches to avoid a super long and awkward
      // animation-like experience
      for (let n = 0; n < this.batchSize; n++) {
        if (this.queue.length < 1) {
          return
        }

        const first = this.queue.shift()

        if (typeof first === 'function') {
          first()
        }
      }
    }

    return setTimeout(this.run, this.queueInterval)
  }
}

export default JobQueue
